import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/** What the user picked: فاتح · داكن · تلقائي — the same choice as the mobile app. */
export type ThemePreference = 'light' | 'dark' | 'system';
export type Scheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'fusilat_theme';

type ThemeValue = {
  scheme: Scheme;
  preference: ThemePreference;
  setPreference: (p: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeValue>({ scheme: 'light', preference: 'system', setPreference: () => {} });

const isPreference = (v: unknown): v is ThemePreference => v === 'light' || v === 'dark' || v === 'system';

function readStored(): ThemePreference {
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(v) ? v : 'system';
  } catch {
    // Private windows and blocked storage: follow the system, never throw.
    return 'system';
  }
}

function systemDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveScheme(pref: ThemePreference, prefersDark: boolean): Scheme {
  if (pref === 'system') return prefersDark ? 'dark' : 'light';
  return pref;
}

/**
 * Puts `dark` on <html> when the night palette applies; every colour in the
 * app is a CSS variable that the `.dark` block in index.css redefines.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPref] = useState<ThemePreference>(readStored);
  const [prefersDark, setPrefersDark] = useState<boolean>(systemDark);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setPrefersDark(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  const scheme = resolveScheme(preference, prefersDark);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', scheme === 'dark');
  }, [scheme]);

  const setPreference = useCallback((p: ThemePreference) => {
    setPref(p);
    try { window.localStorage.setItem(THEME_STORAGE_KEY, p); } catch { /* storage blocked: still applies for this visit */ }
  }, []);

  const value = useMemo(() => ({ scheme, preference, setPreference }), [scheme, preference, setPreference]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  return useContext(ThemeContext);
}
